import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import Logo from "@/components/layout/Logo";
import ThemeToggle from "@/components/widgets/ThemeToggle";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

const productLinks = [
  { to: "/salary-advance", label: "Salary Advance Loan" },
  { to: "/personal-loan", label: "Personal Loan" },
  { to: "/eligibility", label: "Eligibility" },
];
const toolLinks = [
  { to: "/loan-calculator", label: "Loan Calculator" },
  { to: "/emi-calculator", label: "EMI Calculator" },
];

export default function Navbar() {
  const [mobile, setMobile] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const { user } = useAuth();

  return (
    <header className="sticky top-0 z-30 border-b border-border/60 glass" data-testid="site-navbar">
      <div className="page-shell px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-10">
          <Logo />
          <nav className="hidden lg:flex items-center gap-1 text-sm">
            <NavLink to="/" end className={({isActive}) => `px-3 py-2 rounded-md font-medium ${isActive ? "text-primary" : "hover:text-primary"}`} data-testid="nav-home">Home</NavLink>
            <div className="relative" onMouseEnter={() => setProductsOpen(true)} onMouseLeave={() => setProductsOpen(false)}>
              <button className="px-3 py-2 rounded-md font-medium hover:text-primary flex items-center gap-1" data-testid="nav-products">
                Products <ChevronDown className="h-3 w-3" />
              </button>
              {productsOpen && (
                <div className="absolute top-full left-0 pt-2">
                  <div className="w-64 glass rounded-xl p-2 shadow-xl">
                    {productLinks.map((l) => (
                      <Link key={l.to} to={l.to} className="block px-3 py-2 rounded-md text-sm hover:bg-muted" data-testid={`nav-${l.to.slice(1)}`}>{l.label}</Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="relative" onMouseEnter={() => setToolsOpen(true)} onMouseLeave={() => setToolsOpen(false)}>
              <button className="px-3 py-2 rounded-md font-medium hover:text-primary flex items-center gap-1" data-testid="nav-tools">
                Tools <ChevronDown className="h-3 w-3" />
              </button>
              {toolsOpen && (
                <div className="absolute top-full left-0 pt-2">
                  <div className="w-56 glass rounded-xl p-2 shadow-xl">
                    {toolLinks.map((l) => (
                      <Link key={l.to} to={l.to} className="block px-3 py-2 rounded-md text-sm hover:bg-muted" data-testid={`nav-${l.to.slice(1)}`}>{l.label}</Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <NavLink to="/blog" className={({isActive}) => `px-3 py-2 rounded-md font-medium ${isActive ? "text-primary" : "hover:text-primary"}`} data-testid="nav-blog">Blog</NavLink>
            <NavLink to="/about" className={({isActive}) => `px-3 py-2 rounded-md font-medium ${isActive ? "text-primary" : "hover:text-primary"}`} data-testid="nav-about">About</NavLink>
            <NavLink to="/contact" className={({isActive}) => `px-3 py-2 rounded-md font-medium ${isActive ? "text-primary" : "hover:text-primary"}`} data-testid="nav-contact">Contact</NavLink>
          </nav>
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <ThemeToggle />
          {user && user.role ? (
            <Link to={user.role === "admin" ? "/admin" : "/app"} data-testid="nav-dashboard">
              <Button variant="outline" className="rounded-full">Dashboard</Button>
            </Link>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login"><Button variant="ghost" className="rounded-full">Sign in</Button></Link>
              <Link to="/register" data-testid="nav-register">
                <Button className="rounded-full brand-gradient text-white hover:opacity-90">Apply Now</Button>
              </Link>
            </>
          )}
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <ThemeToggle />
          <Button variant="ghost" size="icon" onClick={() => setMobile((v) => !v)} data-testid="nav-mobile-toggle">
            {mobile ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {mobile && (
        <div className="lg:hidden border-t border-border/60 bg-background">
          <div className="px-6 py-4 space-y-1">
            <Link to="/" onClick={() => setMobile(false)} className="block py-2 font-medium">Home</Link>
            {[...productLinks, ...toolLinks, { to: "/blog", label: "Blog" }, { to: "/about", label: "About" }, { to: "/contact", label: "Contact" }].map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setMobile(false)} className="block py-2 text-sm">{l.label}</Link>
            ))}
            <div className="pt-3 flex gap-2">
              {user && user.role ? (
                <Link to={user.role === "admin" ? "/admin" : "/app"} className="flex-1"><Button className="w-full rounded-full brand-gradient text-white">Dashboard</Button></Link>
              ) : (
                <>
                  <Link to="/login" className="flex-1"><Button variant="outline" className="w-full rounded-full">Sign in</Button></Link>
                  <Link to="/register" className="flex-1"><Button className="w-full rounded-full brand-gradient text-white">Apply Now</Button></Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
